// src/engine/layoutEngine.ts
//
// Automatic graph layout using dagre.
// Takes ReactFlow nodes + edges and returns new positions arranged
// in a left-to-right signal flow (sources on left, monitors on right).

import * as dagre from '@dagrejs/dagre'
import { Node, Edge } from '@xyflow/react'
import { DeviceCategory } from '@/types'

// ── Layout direction options ──

export type LayoutDirection = 'LR' | 'TB' // left-to-right or top-to-bottom

// ── Category rank: controls left→right ordering in LR mode ──
// Lower rank = further left (earlier in signal chain)

const CATEGORY_RANK: Partial<Record<DeviceCategory, number>> = {
  controller: 0,
  midi_interface: 1,
  synth: 2,
  drum_machine: 2,
  sampler: 2,
  groovebox: 2,
  semi_modular: 2,
  effects: 3,
  mixer: 4,
  interface: 5,
  monitoring: 6,
  computer: 0,
  usb_hub: 0,
  network_switch: 0,
  recorder: 4,
}

// ── Default node dimensions (used when measured size isn't available) ──

const DEFAULT_NODE_WIDTH = 240
const DEFAULT_NODE_HEIGHT = 100

// ── Main layout function ──

export interface LayoutOptions {
  direction?: LayoutDirection
  nodeSpacing?: number // vertical gap between nodes
  rankSpacing?: number // horizontal gap between ranks (columns)
}

export function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {},
): { nodes: Node[]; edges: Edge[] } {
  const { direction = 'LR', nodeSpacing = 80, rankSpacing = 180 } = options

  if (nodes.length === 0) return { nodes, edges }

  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))

  g.setGraph({
    rankdir: direction,
    nodesep: nodeSpacing,
    ranksep: rankSpacing,
    marginx: 40,
    marginy: 40,
  })

  // Add nodes with their measured or estimated dimensions
  for (const node of nodes) {
    const w = node.measured?.width ?? DEFAULT_NODE_WIDTH
    const h = node.measured?.height ?? DEFAULT_NODE_HEIGHT

    g.setNode(node.id, {
      width: w,
      height: h,
    })
  }

  // Add edges
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target)
  }

  // For disconnected nodes, add invisible edges based on category rank
  // so they still get placed logically in the flow
  const connectedNodes = new Set<string>()
  for (const edge of edges) {
    connectedNodes.add(edge.source)
    connectedNodes.add(edge.target)
  }

  const disconnected = nodes.filter(n => !connectedNodes.has(n.id))
  if (disconnected.length > 0 && connectedNodes.size > 0) {
    // Create a virtual anchor node for each rank tier
    // so disconnected nodes cluster near their category position
    // We do this by finding a connected node at a similar rank
    for (const node of disconnected) {
      const category = (node.data as { category?: DeviceCategory }).category
      const rank = category ? (CATEGORY_RANK[category] ?? 3) : 3

      // Find a connected node at same or adjacent rank
      const anchor = nodes.find(n => {
        if (!connectedNodes.has(n.id)) return false
        const nc = (n.data as { category?: DeviceCategory }).category
        const nr = nc ? (CATEGORY_RANK[nc] ?? 3) : 3
        return Math.abs(nr - rank) <= 1
      })

      if (anchor) {
        // Add a weak invisible edge just for layout purposes
        // (we won't include this in the output edges)
        g.setEdge(anchor.id, node.id, { minlen: 1, weight: 0 })
      }
    }
  }

  // Run dagre layout
  dagre.layout(g)

  // Map positions back to ReactFlow nodes
  const layoutedNodes = nodes.map(node => {
    const dagreNode = g.node(node.id)
    if (!dagreNode) return node

    const w = node.measured?.width ?? DEFAULT_NODE_WIDTH
    const h = node.measured?.height ?? DEFAULT_NODE_HEIGHT

    return {
      ...node,
      position: {
        // dagre gives center position, ReactFlow wants top-left
        x: dagreNode.x - w / 2,
        y: dagreNode.y - h / 2,
      },
    }
  })

  return { nodes: layoutedNodes, edges }
}
